import { useMemo, useState } from "react";
import { Box, Search, MoreVertical, Play, Square, RotateCw, ScrollText, Info, Loader2, PackageCheck } from "lucide-react";
import { api } from "@/data/api";
import { useAsync } from "@/hooks/useAsync";
import { PageHeader, StatusBadge, DataTable, AsyncBoundary, RowsSkeleton, UsageBar, ConfirmDialog } from "@/components/common";
import { AdoptContainerDialog } from "@/components/common/AdoptContainerDialog";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

const STATUS_FILTERS = ["all", "running", "stopped", "paused", "restarting", "missing", "unknown", "error"];
const ADOPTION_FILTERS = ["all", "discovered", "adopted", "managed", "drifted"];

// running -> pode Stop/Restart. stopped/error -> pode Start/Restart. Demais
// estados (missing/unknown/restarting) não permitem nenhuma ação real —
// nunca oferecemos um botão que pareça funcionar sobre um estado que não sabemos ao certo.
const CAN_START = (s) => s === "stopped" || s === "error";
const CAN_STOP = (s) => s === "running";
const CAN_RESTART = (s) => s === "running" || s === "stopped" || s === "error";

export default function Containers() {
  const { data, loading, error, reload } = useAsync(() =>
    Promise.all([api.getContainers(), api.getContainerSummary()]).then(([containers, summary]) => ({ containers, summary })),
  [], 15000);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [adoption, setAdoption] = useState("all");
  const [selected, setSelected] = useState(new Set());
  const [adoptTarget, setAdoptTarget] = useState(null); // { serverId, containerIds }
  const [confirmAction, setConfirmAction] = useState(null); // { row, action }
  const [busyIds, setBusyIds] = useState(new Set());
  const [actionError, setActionError] = useState(null);

  const rows = useMemo(() => {
    if (!data) return [];
    return data.containers.filter((c) =>
      (status === "all" || c.status === status) &&
      (adoption === "all" || c.adoptionState === adoption) &&
      (!q || `${c.name} ${c.image} ${c.project}`.toLowerCase().includes(q.toLowerCase())),
    );
  }, [data, q, status, adoption]);

  const discoveredSelectable = rows.filter((r) => r.adoptionState === "discovered");

  const toggleSelect = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleSelectAll = () => setSelected((s) => (s.size === discoveredSelectable.length ? new Set() : new Set(discoveredSelectable.map((r) => r.id))));

  const runAction = async (row, action) => {
    setBusyIds((s) => new Set(s).add(row.id));
    setActionError(null);
    try {
      const fn = { start: api.startContainer, stop: api.stopContainer, restart: api.restartContainer }[action];
      const job = await fn(row.server, row.id);
      // Job foi despachado — acompanha até um estado terminal antes de
      // considerar concluído (nunca mostra sucesso antes da confirmação real).
      await pollJob(job.id);
    } catch (e) {
      setActionError(`${row.name}: ${e.message}`);
    } finally {
      setBusyIds((s) => { const n = new Set(s); n.delete(row.id); return n; });
      reload();
    }
  };

  const pollJob = async (jobId, attempts = 15) => {
    for (let i = 0; i < attempts; i++) {
      const job = await api.getJob(jobId);
      if (["succeeded", "failed", "denied", "timed_out"].includes(job.status)) {
        if (job.status !== "succeeded") throw new Error(job.result?.error || `job ${job.status}`);
        return job;
      }
      await new Promise((r) => setTimeout(r, 800));
    }
    throw new Error("tempo esgotado aguardando confirmação do Agent");
  };

  const columns = [
    { key: "select", header: "", tdClassName: "w-8", render: (r) => r.adoptionState === "discovered" ? (
      <input type="checkbox" checked={selected.has(r.id)} onChange={() => toggleSelect(r.id)} onClick={(e) => e.stopPropagation()} aria-label={`Selecionar ${r.name}`} />
    ) : null },
    { key: "name", header: "Nome", render: (r) => (
      <div><p className="font-medium text-foreground">{r.name}</p><p className="font-mono text-[11px] text-muted-foreground">{r.image}</p></div>
    ) },
    { key: "project", header: "Projeto" },
    { key: "server", header: "Servidor", render: (r) => <span className="font-mono text-xs">{r.server}</span> },
    { key: "adoption", header: "Adoção", render: (r) => <StatusBadge status={r.adoptionState} size="sm" pulse={false} /> },
    { key: "status", header: "Status", render: (r) => <StatusBadge status={r.status} size="sm" /> },
    { key: "cpu", header: "CPU", tdClassName: "w-28", render: (r) => <UsageBar pct={r.cpu * 4} showValue={false} /> },
    { key: "mem", header: "Memória", align: "right", render: (r) => <span className="font-mono text-xs">{r.memUsed}/{r.memTotal} MB</span> },
    { key: "actions", header: "", align: "right", render: (r) => {
      const busy = busyIds.has(r.id);
      if (r.adoptionState === "discovered") {
        return (
          <Button size="sm" variant="outline" onClick={() => setAdoptTarget({ serverId: r.server, containerIds: [r.id] })} className="border-line bg-transparent hover:bg-white/5" data-testid={`adopt-${r.id}`}>
            <PackageCheck className="mr-1.5 h-3.5 w-3.5" /> Adotar
          </Button>
        );
      }
      return (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button data-testid={`container-actions-${r.id}`} disabled={busy} className="flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-white/5 hover:text-foreground disabled:opacity-40">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <MoreVertical className="h-4 w-4" />}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="border-line bg-popover">
            <DropdownMenuItem disabled={busy || !CAN_START(r.status)} onClick={() => runAction(r, "start")}><Play className="mr-2 h-4 w-4 text-ok" />Start</DropdownMenuItem>
            <DropdownMenuItem disabled={busy || !CAN_STOP(r.status)} onClick={() => setConfirmAction({ row: r, action: "stop" })}><Square className="mr-2 h-4 w-4 text-err" />Stop</DropdownMenuItem>
            <DropdownMenuItem disabled={busy || !CAN_RESTART(r.status)} onClick={() => setConfirmAction({ row: r, action: "restart" })}><RotateCw className="mr-2 h-4 w-4 text-warn" />Restart</DropdownMenuItem>
            <DropdownMenuItem disabled className="opacity-50"><ScrollText className="mr-2 h-4 w-4" />Logs (em breve)</DropdownMenuItem>
            <DropdownMenuItem disabled className="opacity-50"><Info className="mr-2 h-4 w-4" />Inspect (em breve)</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      );
    } },
  ];

  return (
    <div className="space-y-6 animate-fade-up">
      <PageHeader
        icon={Box}
        title="Containers"
        description="Containers Docker descobertos pelo Agent em todos os servidores. Recursos Discovered são somente leitura até adoção explícita."
        breadcrumb={<><span>Infraestrutura</span><span>/</span><span className="text-foreground">Containers</span></>}
      />

      {!loading && data && (
        <div className="flex flex-wrap items-center gap-2">
          {[
            { k: "running", label: "Ativos", tone: "ok" }, { k: "paused", label: "Pausados", tone: "warn" },
            { k: "stopped", label: "Parados", tone: "neutral" }, { k: "error", label: "Erro", tone: "err" },
          ].map((s) => (
            <div key={s.k} className="flex items-center gap-2 rounded-lg border border-line bg-surface px-3 py-2">
              <span className={cn("h-2 w-2 rounded-full", { ok: "bg-ok", warn: "bg-warn", err: "bg-err", neutral: "bg-muted-foreground" }[s.tone])} />
              <span className="text-sm text-muted-foreground">{s.label}</span>
              <span className="font-mono text-sm font-semibold text-foreground">{data.summary[s.k] ?? 0}</span>
            </div>
          ))}
        </div>
      )}

      {actionError && <p role="alert" className="rounded-lg border border-err/30 bg-err/10 px-3 py-2 text-sm text-err">{actionError}</p>}

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-[200px] flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar container, imagem, projeto..." data-testid="containers-search" className="border-line bg-surface pl-9" />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-40 border-line bg-surface" data-testid="containers-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent className="border-line bg-popover">
            {STATUS_FILTERS.map((s) => <SelectItem key={s} value={s}>{s === "all" ? "Todos status" : s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={adoption} onValueChange={setAdoption}>
          <SelectTrigger className="w-44 border-line bg-surface" data-testid="containers-adoption-filter"><SelectValue /></SelectTrigger>
          <SelectContent className="border-line bg-popover">
            {ADOPTION_FILTERS.map((s) => <SelectItem key={s} value={s}>{s === "all" ? "Toda adoção" : s}</SelectItem>)}
          </SelectContent>
        </Select>
        {discoveredSelectable.length > 0 && (
          <label className="ml-auto flex items-center gap-1.5 text-xs text-muted-foreground">
            <input type="checkbox" checked={selected.size > 0 && selected.size === discoveredSelectable.length} onChange={toggleSelectAll} />
            Selecionar todos Discovered
          </label>
        )}
        {selected.size > 0 && (
          <Button size="sm" onClick={() => setAdoptTarget({ serverId: rows.find((r) => selected.has(r.id))?.server, containerIds: [...selected] })} className="bg-brand text-white hover:bg-brand-hover" data-testid="adopt-selected">
            <PackageCheck className="mr-1.5 h-4 w-4" /> Adotar Selecionados ({selected.size})
          </Button>
        )}
      </div>

      <AsyncBoundary loading={loading} error={error} skeleton={<RowsSkeleton rows={8} />}>
        <DataTable columns={columns} rows={rows} testid="containers-table" />
      </AsyncBoundary>

      {adoptTarget && (
        <AdoptContainerDialog
          open={!!adoptTarget}
          onOpenChange={(o) => !o && setAdoptTarget(null)}
          serverId={adoptTarget.serverId}
          containerIds={adoptTarget.containerIds}
          onAdopted={() => { setSelected(new Set()); setAdoptTarget(null); reload(); }}
        />
      )}

      <ConfirmDialog
        open={!!confirmAction}
        onOpenChange={(o) => !o && setConfirmAction(null)}
        title={confirmAction ? `${confirmAction.action === "stop" ? "Parar" : "Reiniciar"} ${confirmAction.row.name}?` : ""}
        description={confirmAction?.action === "stop" ? "O container ficará indisponível até ser iniciado novamente." : "O container poderá ficar temporariamente indisponível durante a reinicialização."}
        confirmLabel={confirmAction?.action === "stop" ? "Parar Container" : "Reiniciar Container"}
        destructive={confirmAction?.action === "stop"}
        onConfirm={() => { runAction(confirmAction.row, confirmAction.action); setConfirmAction(null); }}
      />
    </div>
  );
}
